import { t } from 'i18next';
import * as yup from 'yup';

import { getEmptyErrorMessage } from 'core/yup';
import { QuoteEntityType, VendorQuoteAttachToType } from 'quotes/enums';

import { VENDOR_QUOTE_REVISION_MAX_LENGTH } from './utils';

interface IImportVendorQuoteSchemaOptions {
  isProjectRequired?: boolean;
  isVendorRequired?: boolean;
  isVendorQuoteNumberRequired?: boolean;
  isVendorQuoteRevisionShown?: boolean;
}

export const importVendorQuoteSchema = ({
  isProjectRequired = true,
  isVendorRequired = false,
  isVendorQuoteNumberRequired = false,
  isVendorQuoteRevisionShown = false,
}: IImportVendorQuoteSchemaOptions = {}) => {
  const project = yup.object().label(t('labelProject')).nullable();
  // `.default(undefined)` keeps an unrendered vendor out of the validated values — a yup object
  // otherwise defaults to `{}`, which the payload mapper would then have to special-case.
  const vendorLocation = yup.object().label(t('labelVendor')).nullable().default(undefined);
  const externalNumber = yup.string().label(t('labelVendorQuoteNumber')).nullable();
  const revisionNumber = yup.string().label(t('labelVendorQuoteRevision')).nullable();

  return yup.object().shape({
    // Project is only required where it's actually rendered. On the contact-only form it's hidden
    // and may legitimately be null (no context project and a null BE-resolved project_id), so
    // requiring it would block submit on a field the user can't fill.
    project: isProjectRequired
      ? project.required(getEmptyErrorMessage(t('labelProject')))
      : project,
    // Required only for alternate-version importers, who are the cohort the vendor AP snapshot is
    // written for; everyone else is never shown the field.
    vendorLocation: isVendorRequired
      ? vendorLocation.required(getEmptyErrorMessage(t('labelVendor')))
      : vendorLocation,
    jobName: yup
      .string()
      .label(t('labelJobName'))
      .nullable()
      .required(getEmptyErrorMessage(t('labelJobName'))),
    contact: yup
      .object()
      .label(t('labelVendorQuoteContact', { entityType: QuoteEntityType.QUOTE }))
      .nullable()
      .required(getEmptyErrorMessage(t('labelVendorQuoteContact'))),
    externalProjectName: yup.string().label(t('labelVendorProjectName')).nullable(),
    // The BE checks the trimmed number, so whitespace alone must not pass here either.
    externalNumber: isVendorQuoteNumberRequired
      ? externalNumber.trim().required(getEmptyErrorMessage(t('labelVendorQuoteNumber')))
      : externalNumber,
    // Capped only where it renders, so a hidden extracted value can never block the submit.
    revisionNumber: isVendorQuoteRevisionShown
      ? revisionNumber.trim().max(
          VENDOR_QUOTE_REVISION_MAX_LENGTH,
          t('errorFieldMaxLength', {
            field: t('labelVendorQuoteRevision'),
            length: VENDOR_QUOTE_REVISION_MAX_LENGTH,
          })
        )
      : revisionNumber,
    attachTo: yup
      .string()
      .label(t('labelAttachTo'))
      .oneOf(Object.values(VendorQuoteAttachToType))
      .nullable()
      .required(getEmptyErrorMessage(t('labelAttachTo'))),
  });
};
